/**
 * Cost Analytics — one page, two questions.
 *
 * These were two sidebar entries, which put a decision in front of the user
 * before they had the information to make it: "Cost Analytics" and "Unit
 * Costs" both mean spend, and nothing in either label says which one answers
 * the question you arrived with. They are the same subject at two
 * granularities —
 *
 *   Cloud spend       — where the bill went (provider, service, category, trend)
 *   Service catalogue — every billed service, what it costs, and what 100 calls
 *                       of it cost
 *
 * so they belong behind one nav item with a tab between them.
 *
 * Neither page is modified: both are mounted here exactly as they were, and
 * the old `/unit-costs` route still resolves (to this shell, on the unit tab)
 * so existing links and bookmarks keep working.
 */
import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Calculator, DollarSign } from 'lucide-react';

import CostAnalytics from './CostAnalytics';
import UnitCosts from './UnitCosts';

type TabKey = 'cloud' | 'unit';

const TABS: { key: TabKey; label: string; hint: string; icon: typeof DollarSign }[] = [
  { key: 'cloud', label: 'Cloud spend', hint: 'Where the bill went', icon: DollarSign },
  {
    key: 'unit',
    label: 'Service catalogue',
    hint: 'What each service costs to run',
    icon: Calculator,
  },
];

interface Props {
  /** Tab shown when the URL carries no `?tab=`. Set by the route. */
  defaultTab?: TabKey;
}

export default function Costs({ defaultTab = 'cloud' }: Props) {
  const [searchParams, setSearchParams] = useSearchParams();

  const active: TabKey = useMemo(() => {
    const raw = searchParams.get('tab');
    return raw === 'cloud' || raw === 'unit' ? raw : defaultTab;
  }, [searchParams, defaultTab]);

  /**
   * Switching tabs preserves the rest of the query string except `service`,
   * which belongs to the unit tab's modal — carrying it over to cloud spend
   * would leave a stale param that reopens a service on the way back.
   */
  const select = (key: TabKey) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', key);
    if (key !== 'unit') next.delete('service');
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="space-y-6">
      <nav
        className="flex flex-wrap gap-2 border-b border-slate-200 pb-px dark:border-slate-800"
        aria-label="Cost views"
      >
        {TABS.map(({ key, label, hint, icon: Icon }) => {
          const isActive = key === active;
          return (
            <button
              key={key}
              type="button"
              onClick={() => select(key)}
              aria-current={isActive ? 'page' : undefined}
              className={`-mb-px flex items-center gap-2 rounded-t-lg border-b-2 px-4 py-2.5 text-sm transition ${
                isActive
                  ? 'border-brand-500 text-brand-600 dark:text-brand-400'
                  : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'
              }`}
            >
              <Icon className="h-4 w-4 flex-none" />
              <span className="font-medium">{label}</span>
              <span className="hidden text-xs text-slate-400 sm:inline">· {hint}</span>
            </button>
          );
        })}
      </nav>

      {/* Mounted conditionally, not hidden with CSS: the inactive tab must not
          run its queries, and the unit-cost endpoints are not free to call. */}
      {active === 'cloud' ? <CostAnalytics /> : <UnitCosts />}
    </div>
  );
}
